import React, { useEffect, useState } from "react";
import * as Scrivito from "scrivito";
import { getHomepage, getPageLink, getPresentObjSearch } from "utils";
import { useTranslate } from "hooks";
import { Icon } from "./Icon/Icon";

type BreadcrumbProps = {
  page?: Scrivito.Obj | null;
};

type SpecialPageClass = "JobItem" | "NewsItem" | "EventItem";

const isScrivitoObj = (
  value: Scrivito.Obj | null | undefined,
): value is Scrivito.Obj => Boolean(value);

const isSpecialPageClass = (objClass: string): objClass is SpecialPageClass =>
  objClass === "JobItem" || objClass === "NewsItem" || objClass === "EventItem";

export const Breadcrumb = Scrivito.connect(({ page }: BreadcrumbProps) => {
  const translate = useTranslate();
  const [ancestors, setAncestors] = useState<Scrivito.Obj[]>([]);

  useEffect(() => {
    let isCancelled = false;
    Scrivito.load(() => {
      let items: Scrivito.Obj[] = [];

      if (!page) {
        return items;
      }

      const pageObjClass = page.objClass();

      if (page.path()) {
        items = page.ancestors().concat([page]).filter(isScrivitoObj);
      } else if (!page.path() && isSpecialPageClass(pageObjClass)) {
        const homepagePath = getHomepage()?.path();
        if (!homepagePath) {
          return items;
        }

        const parentMap: Record<SpecialPageClass, string> = {
          JobItem: "JobList",
          NewsItem: "NewsList",
          EventItem: "EventList",
        };

        const listPageParent = getPresentObjSearch()
          .and("_objClass", "equals", parentMap[pageObjClass])
          .and("_path", "startsWith", homepagePath)
          .toArray()[0];

        items = listPageParent
          ? listPageParent
              .ancestors()
              .concat([listPageParent, page])
              .filter(isScrivitoObj)
          : [];
      } else {
        items = [getHomepage(), page].filter(isScrivitoObj);
      }

      return items.filter(
        (item) => item.objClass() !== "Root" && item.objClass() !== "Website",
      );
    }).then((items: Scrivito.Obj[]) => {
      if (!isCancelled) {
        setAncestors(items);
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [page]);

  return (
    <nav className="breadcrumb-nav">
      <ol className="breadcrumb-list container">
        {ancestors.map((anc, idx) => {
          const classNames: string[] = ["breadcrumb-item"];
          if (idx === 0) {
            classNames.push("home");
          }
          const pageTitle = anc.get("title");
          const title =
            idx === 0 ? (
              <Icon iconName="home-regular" />
            ) : (
              (typeof pageTitle === "string" && pageTitle) ||
              translate("PAGE_NO_PAGE_TITLE")
            );
          const resolvedLink = getPageLink(anc);
          const ancLink: React.ComponentProps<typeof Scrivito.LinkTag>["to"] =
            resolvedLink === undefined ? undefined : resolvedLink;

          return (
            <li key={anc.id()}>
              <Scrivito.LinkTag to={ancLink} className={classNames.join(" ")}>
                {idx !== 0 && <Icon iconName="chevron-right-regular" />}
                <span>{title}</span>
              </Scrivito.LinkTag>
            </li>
          );
        })}
      </ol>
    </nav>
  );
});
